import React, { useState, useContext } from 'react';
import { alpha } from '@mui/material/styles';
import { Tabs, Tab, Box, useMediaQuery, useTheme, Paper, BottomNavigation, BottomNavigationAction, Typography, Toolbar, AppBar } from '@mui/material';
import Logo from './brand/Logo';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import BarChartIcon from '@mui/icons-material/BarChart';
import Calendar from './Calendar';
import AuxiliaireManagement from './AuxiliaireManagement';
import MonthlyStatistics from './MonthlyStatistics';
import PriceCategoryManagement from './PriceCategoryManagement';
import AccountSettings from './AccountSettings';
import AccountMenu from './AccountMenu';
import { AuthContext } from '../contexts/AuthContext';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// Theme definition can stay here or move to a separate file
function MainLayout() {
  const [tabValue, setTabValue] = useState(0);
  // Was a second, hardcoded light-mode theme declared here purely to read a
  // breakpoint, which meant this file could never follow dark mode. Use the
  // real theme from context instead.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Add check for auth context
  if (!auth) {
    throw new Error("MainLayout must be used within an AuthProvider");
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Navigate to the corresponding route
    switch (newValue) {
      case 0:
        navigate('/');
        break;
      case 1:
        navigate('/auxiliaires');
        break;
      case 2:
        navigate('/prix');
        break;
      case 3:
        navigate('/statistiques');
        break;
    }
  };

  const navigationItems = React.useMemo(() => ([
    // shortLabel keeps the mobile bar to one line per item; the full label
    // is what the desktop tabs show.
    { label: "Calendrier", shortLabel: "Planning", icon: <CalendarMonthIcon />, path: "/" },
    { label: "Gestion Aux.", shortLabel: "Équipe", icon: <PeopleIcon />, path: "/auxiliaires" },
    { label: "Gestion Prix", shortLabel: "Tarifs", icon: <PriceCheckIcon />, path: "/prix" },
    { label: "Statistiques", shortLabel: "Stats", icon: <BarChartIcon />, path: "/statistiques" },
  ]), []);

  // Update tab value based on current route
  React.useEffect(() => {
    const currentPath = location.pathname;
    const index = navigationItems.findIndex(item => item.path === currentPath);
    if (index !== -1) {
      setTabValue(index);
    }
  }, [location.pathname, navigationItems]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      {!isMobile && (
          <AppBar position="sticky">
            <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, minWidth: 0 }}>
                <Logo size={30} />
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="app navigation tabs"
                  sx={{ ml: 2 }}
                  textColor="primary"
                  indicatorColor="primary"
                >
                  {navigationItems.map((item, index) => (
                    <Tab key={index} label={item.label} />
                  ))}
                </Tabs>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountMenu />
              </Box>
            </Toolbar>
          </AppBar>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // The mobile bar is fixed at bottom:12 and about 56px tall, so
            // without this the last card of any list sits underneath it.
            // env(safe-area-inset-bottom) keeps it clear of the home indicator.
            pb: isMobile
              ? 'calc(80px + env(safe-area-inset-bottom, 0px))'
              : 0,
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="h-full"
            >
              <Routes>
                <Route path="/" element={<Calendar />} />
                <Route path="/auxiliaires" element={<AuxiliaireManagement />} />
                <Route path="/prix" element={<PriceCategoryManagement />} />
                <Route path="/statistiques" element={<MonthlyStatistics />} />
                <Route path="/account" element={<AccountSettings />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </Box>

        {isMobile && (
          <Paper
            elevation={0}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              borderRadius: 0,
              overflow: 'hidden',
              zIndex: (t) => t.zIndex.appBar,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: (t) => alpha(t.palette.background.paper, 0.92),
              backdropFilter: 'blur(12px)',
              // Sit above the iOS home indicator instead of under it.
              pb: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <BottomNavigation
              showLabels
              value={tabValue}
              onChange={handleTabChange}
            >
              {navigationItems.map((item, index) => (
                <BottomNavigationAction
              key={index}
              label={item.shortLabel ?? item.label}
              icon={item.icon}
              sx={{
                minWidth: 0,
                px: 0.5,
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&.Mui-selected': { fontSize: '0.7rem' },
                },
              }}
            />
              ))}
            </BottomNavigation>
          </Paper>
        )}

        <Box
          component="footer"
          sx={{
            py: 2,
            mt: 'auto',
            textAlign: 'center',
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © Yannis Tevissen 2025
          </Typography>
        </Box>
    </Box>
  );
}

export default MainLayout; 